import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { api } from '@/lib/api';
import {
  DatosGasto, EstadoGasto, Gasto, PropiedadGasto,
  ETIQUETAS_ESTADO, hoyISO, opcionesCategoria,
} from './tipos';

interface Props {
  abierto: boolean;
  /** El gasto a editar, o null para dar uno de alta. */
  gasto: Gasto | null;
  propiedades: PropiedadGasto[];
  onCerrar: () => void;
  onGuardado: () => void;
}

interface Formulario {
  propertyId: string;
  expenseDate: string;
  amount: string;
  concept: string;
  category: string;
  status: EstadoGasto;
  observations: string;
}

function formularioVacio(): Formulario {
  return {
    propertyId: '',
    expenseDate: hoyISO(),
    amount: '',
    concept: '',
    category: '',
    status: 'PENDIENTE',
    observations: '',
  };
}

/**
 * Alta y edición de un gasto de mantenimiento, en el mismo formulario.
 *
 * En la app de lavandería son dos pantallas distintas —una pestaña «Nuevo Gasto»
 * y un modal de edición— con catálogos de categoría diferentes cada una. Aquí es
 * un solo formulario, así que lo que se ve al crear es lo que se ve al editar.
 */
export default function ModalGasto({ abierto, gasto, propiedades, onCerrar, onGuardado }: Props) {
  const editando = Boolean(gasto);
  const [form, setForm] = useState<Formulario>(formularioVacio);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!abierto) return;
    setError(null);
    setForm(
      gasto
        ? {
            propertyId: gasto.propertyId,
            expenseDate: gasto.expenseDate,
            amount: gasto.amount ? String(gasto.amount) : '',
            concept: gasto.concept,
            category: gasto.category || '',
            // Un gasto borrado no llega a este modal, pero el tipo lo admite.
            status: gasto.status === 'deleted' ? 'PENDIENTE' : gasto.status,
            observations: gasto.observations || '',
          }
        : formularioVacio(),
    );
  }, [abierto, gasto]);

  const categorias = useMemo(() => opcionesCategoria(gasto?.category), [gasto]);

  /**
   * El nombre se resuelve contra el listado de Hostify al guardar, no se copia
   * del documento: el `propertyName` guardado es el que tenía el piso el día del
   * alta, y si se ha renombrado en el PMS, editarlo es buen momento para
   * refrescarlo. Si el listing ya no está en la lista, se conserva el que había.
   */
  const nombrePropiedad = (id: string) =>
    propiedades.find((p) => p.id === id)?.nickname || gasto?.propertyName || id;

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.propertyId) return setError('Selecciona una propiedad');
    if (!form.concept.trim()) return setError('El concepto es obligatorio');
    if (!form.expenseDate) return setError('La fecha del gasto es obligatoria');
    const importe = form.amount === '' ? 0 : Number(form.amount);
    if (!Number.isFinite(importe) || importe < 0) return setError('El importe no puede ser negativo');

    const datos: DatosGasto = {
      propertyId: form.propertyId,
      propertyName: nombrePropiedad(form.propertyId),
      amount: importe,
      concept: form.concept.trim(),
      observations: form.observations.trim(),
      expenseDate: form.expenseDate,
      category: form.category,
      status: form.status,
    };

    setGuardando(true);
    setError(null);
    try {
      if (gasto) await api.actualizarGasto(gasto.id, gasto.propertyId, datos);
      else await api.crearGasto(datos);
      onGuardado();
      onCerrar();
    } catch (err: any) {
      setError(err?.message || 'No se ha podido guardar el gasto');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal
      isOpen={abierto}
      onClose={onCerrar}
      title={editando ? 'Editar gasto' : 'Nuevo gasto de mantenimiento'}
      size="lg"
    >
      <form onSubmit={guardar} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label" htmlFor="gasto-propiedad">Propiedad *</label>
            <select
              id="gasto-propiedad"
              className="input min-h-11"
              value={form.propertyId}
              onChange={(e) => setForm((f) => ({ ...f, propertyId: e.target.value }))}
              /* La propiedad es la clave de partición del documento en Cosmos, y
                 no se puede cambiar en una edición: mover un gasto de piso sería
                 borrarlo y crear otro, con id nuevo, y las liquidaciones que ya
                 lo referencian apuntan al viejo. */
              disabled={editando || guardando}
            >
              <option value="">Seleccionar propiedad…</option>
              {/* Si el gasto es de un listing que ya no está activo en el PMS, su
                  opción no vendría en la lista y el select saldría en blanco. */}
              {editando && !propiedades.some((p) => p.id === form.propertyId) && (
                <option value={form.propertyId}>{gasto?.propertyName}</option>
              )}
              {propiedades.map((p) => (
                <option key={p.id} value={p.id}>{p.nickname}</option>
              ))}
            </select>
            {editando && (
              <p className="mt-1 text-xs text-sereno-dark/40">
                La propiedad no se puede cambiar. Bórralo y créalo de nuevo si estaba mal.
              </p>
            )}
          </div>

          <div>
            <label className="label" htmlFor="gasto-fecha">Fecha del gasto *</label>
            <input
              id="gasto-fecha"
              type="date"
              className="input min-h-11"
              value={form.expenseDate}
              onChange={(e) => setForm((f) => ({ ...f, expenseDate: e.target.value }))}
              disabled={guardando}
            />
          </div>
        </div>

        <div>
          <label className="label" htmlFor="gasto-concepto">Concepto *</label>
          <input
            id="gasto-concepto"
            type="text"
            className="input min-h-11"
            placeholder="Qué se ha hecho o comprado"
            value={form.concept}
            onChange={(e) => setForm((f) => ({ ...f, concept: e.target.value }))}
            disabled={guardando}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="label" htmlFor="gasto-importe">Importe (€)</label>
            <input
              id="gasto-importe"
              type="number"
              step="0.01"
              min="0"
              className="input min-h-11"
              placeholder="0,00"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              disabled={guardando}
            />
            {/* Se puede dejar vacío a propósito: un gasto se apunta el día que
                ocurre y a veces la factura llega después. */}
            <p className="mt-1 text-xs text-sereno-dark/40">Déjalo vacío si aún no lo sabes.</p>
          </div>

          <div>
            <label className="label" htmlFor="gasto-categoria">Categoría</label>
            <select
              id="gasto-categoria"
              className="input min-h-11"
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              disabled={guardando}
            >
              <option value="">Sin categoría</option>
              {categorias.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Al crear no se pregunta: un gasto nace pendiente de cobrar, y para
              cobrarlo está el botón de la lista, que además pide el importe. */}
          {editando && (
            <div>
              <label className="label" htmlFor="gasto-estado">Estado</label>
              <select
                id="gasto-estado"
                className="input min-h-11"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as EstadoGasto }))}
                disabled={guardando}
              >
                {Object.entries(ETIQUETAS_ESTADO).map(([valor, etiqueta]) => (
                  <option key={valor} value={valor}>{etiqueta}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div>
          <label className="label" htmlFor="gasto-observaciones">Observaciones</label>
          <textarea
            id="gasto-observaciones"
            rows={3}
            className="input resize-y"
            placeholder="Detalle adicional (opcional)"
            value={form.observations}
            onChange={(e) => setForm((f) => ({ ...f, observations: e.target.value }))}
            disabled={guardando}
          />
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* `col-reverse` en el móvil: el orden del DOM deja Cancelar primero
            para el foco del teclado, y al invertirlo visualmente la acción
            principal queda arriba y Cancelar debajo, que es donde cae el
            pulgar. Se confirma queriendo, no de un roce. En escritorio, fila
            con la primaria a la derecha, como el resto del CRM. */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          <button type="button" onClick={onCerrar} disabled={guardando} className="btn-ghost min-h-11 text-sm">
            Cancelar
          </button>
          <button type="submit" disabled={guardando} className="btn-primary min-h-11 text-sm flex items-center justify-center gap-2">
            {guardando && <Loader2 size={15} className="animate-spin" />}
            {editando ? 'Guardar cambios' : 'Crear gasto'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
